import React from "react";

// NOTE: contact is intentionally simple (no form). Portfolio entries are real.
import ParticleBurstCanvas from "@/components/ParticleBurstCanvas";
import { ArrowRight, Bot, Building2, Cpu, Landmark, Mail, Menu } from "lucide-react";

import logoDark from "@/assets/logo-dark.png";

import { siteContent } from "@/content";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";


import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";


const iconMap = {
  Cpu,
  Bot,
  Building2,
  Landmark,
};

const StackedMix = () => {
  return (
    <div className="mt-4 overflow-hidden rounded-none border border-white/10 bg-black/40">
      <div className="flex h-3 w-full">
        <div
          className="h-full bg-[var(--brand-primary)]"
          style={{ width: "50%" }}
          aria-label="Deep Tech + AI (dominant)"
        />
        <div
          className="h-full bg-white/40"
          style={{ width: "25%" }}
          aria-label="Enterprise Software"
        />
        <div
          className="h-full bg-white/25"
          style={{ width: "25%" }}
          aria-label="Fintech"
        />
      </div>
    </div>
  );
};


const Section = ({ id, eyebrow, title, description, children, className }) => {
  return (
    <section id={id} className={cn("scroll-mt-24", className)}>
      <div className="mx-auto w-full max-w-7xl px-5 md:px-10">
        <div className="mb-10 md:mb-14">
          {eyebrow ? (
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold tracking-[0.22em] text-white/65">
              <span className="h-[1px] w-7 bg-white/20" />
              <span className="uppercase">{eyebrow}</span>
            </div>
          ) : null}
          {title ? (
            <h2 className="text-balance text-3xl font-semibold leading-tight tracking-[-0.02em] text-white md:text-4xl">
              {title}
            </h2>
          ) : null}
          {description ? (
            <p className="mt-3 max-w-3xl text-pretty text-base leading-relaxed text-white/70 md:text-lg">
              {description}
            </p>
          ) : null}
        </div>
        {children}
      </div>
    </section>
  );
};

const SiteHeader = ({ nav }) => {
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black">
      <div className="mx-auto flex h-[96px] max-w-7xl items-center justify-between px-5 md:px-10">
        <a href="#top" className="flex items-center gap-3">
          <img
            src={logoDark}
            alt="Sixth Horizon"
            className="h-[96px] w-auto select-none"
            draggable={false}
          />
        </a>

        <nav className="hidden items-center gap-7 md:flex">
          {nav.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="text-sm font-medium text-white/70 transition-colors hover:text-white">
              {n.label}
            </a>
          ))}
          <Button
            asChild
            className="rounded-none bg-[var(--brand-primary)] px-5 text-[13px] font-semibold text-black shadow-[0_0_0_1px_rgba(0,0,0,0.15)] transition-colors hover:bg-white hover:text-black">
            <a href="#contact">Contact</a>
          </Button>
        </nav>

        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                className="rounded-none border-white/15 bg-white/5 text-white hover:bg-white hover:text-black">
                <Menu className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="border-white/10 bg-black/90 text-white backdrop-blur-xl">
              <SheetHeader>
                <SheetTitle className="text-white">Navigate</SheetTitle>
              </SheetHeader>
              <div className="mt-6 grid gap-3">
                {nav.map((n) => (
                  <a
                    key={n.href}
                    href={n.href}
                    className="rounded-md border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white/80 transition-colors hover:bg-white hover:text-black">
                    {n.label}
                  </a>
                ))}
                <a
                  href="#contact"
                  className="mt-2 inline-flex items-center justify-between rounded-none bg-[var(--brand-primary)] px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-white">
                  Contact
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

const GlassCard = ({ className, children }) => (
  <Card
    className={cn(
      "rounded-none border-white/10 bg-white/5 backdrop-blur-[18px]",
      "shadow-[0_0_0_1px_rgba(255,255,255,0.06),_0_18px_60px_rgba(0,0,0,0.55)]",
      "transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:border-white/20 hover:shadow-[0_0_0_1px_rgba(255,255,255,0.09),_0_22px_70px_rgba(0,0,0,0.6)]",
      className
    )}>
    {children}
  </Card>
);

const ContactBlock = () => {
  const email = siteContent.company.email;

  return (
    <div className="grid gap-5 md:grid-cols-12">
      <GlassCard className="p-7 md:col-span-7">
        <p className="text-sm font-semibold text-white/50 uppercase tracking-wider">Direct Email</p>
        <p className="mt-4 text-3xl font-semibold tracking-[-0.03em] text-white md:text-4xl">
          <a
            className="text-[var(--brand-primary)] transition-colors hover:text-white"
            href={`mailto:${email}`}>
            {email}
          </a>
        </p>
        <p className="mt-6 text-sm leading-relaxed text-white/70">
          If there’s a fit, we’ll reply with next steps. Please include enough
          context so we can route your message quickly.
        </p>
      </GlassCard>

      <GlassCard className="p-7 md:col-span-5">
        <p className="text-sm font-semibold text-white">What to include</p>
        <ul className="mt-4 grid gap-2 text-sm text-white/70">
          <li className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--brand-primary)]" />
            A one-pager or deck link
          </li>
          <li className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--brand-primary)]" />
            Your technical differentiator (IP/data/manufacturing edge)
          </li>
          <li className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--brand-primary)]" />
            Current traction + next milestones
          </li>
          <li className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--brand-primary)]" />
            What you’re asking for (investment, consulting, partnerships)
          </li>
        </ul>

        <div className="mt-7 rounded-none border border-white/10 bg-black/40 px-5 py-4">
          <p className="text-xs font-semibold tracking-[0.18em] text-white/60 uppercase">
            Tip
          </p>
          <p className="mt-2 text-sm text-white/70">
            Keep it concise. A clear ask + a link beats a long email.
          </p>
        </div>
      </GlassCard>
    </div>
  );
};

export default function LandingPage() {

  const nav = [
    { label: "Thesis", href: "#thesis" },
    { label: "Services", href: "#services" },
    { label: "Portfolio", href: "#portfolio" },
  ];

  return (
    <div id="top" className="min-h-screen bg-black text-white">
      <SiteHeader nav={nav} />

      {/* Background: Spline as ambient layer */}
      <div aria-hidden="true" className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-black" />
        <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(to_right,rgba(255,255,255,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.14)_1px,transparent_1px)] [background-size:110px_110px]" />
        <div className="absolute inset-0 opacity-[0.12] [background-image:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%22200%22%20height%3D%22200%22%3E%3Cfilter%20id%3D%22n%22%3E%3CfeTurbulence%20type%3D%22fractalNoise%22%20baseFrequency%3D%220.8%22%20numOctaves%3D%224%22%20stitchTiles%3D%22stitch%22/%3E%3C/filter%3E%3Crect%20width%3D%22200%22%20height%3D%22200%22%20filter%3D%22url(%23n)%22%20opacity%3D%220.34%22/%3E%3C/svg%3E')] mix-blend-overlay" />

        {/* Animated background: particle burst (direct cursor interaction) */}
        <div className="absolute inset-0 opacity-[1]">
          <ParticleBurstCanvas className="absolute inset-0" density={320} />
        </div>

        {/* Darkening veil for legibility (force fully black by bottom of viewport) */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_10%,rgba(0,0,0,0.06),rgba(0,0,0,0.66)_48%,rgba(0,0,0,0.92)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_0%,rgba(0,0,0,0.52)_52%,rgba(0,0,0,1)_100%)]" />

        {/* Accent (starts right below header; fades smoothly on ultra-wide) */}
        <div className="absolute inset-x-0 top-[96px] h-px bg-[rgba(0,255,133,0.32)]" />
        <div
          className="absolute left-0 right-0 top-[96px] h-[100vh] opacity-[0.46]
          [mask-image:linear-gradient(to_bottom,black_0%,black_22%,transparent_58%)]
          [-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_22%,transparent_58%)]
          bg-[radial-gradient(circle_at_12%_-18%,rgba(0,255,133,0.32),transparent_68%),radial-gradient(circle_at_52%_-10%,rgba(0,255,133,0.10),transparent_74%)]"
        />
      </div>

      <main className="relative z-10 pt-[96px]">
        {/* Hero */}
        <section className="pb-14 pt-10">
          <div className="mx-auto w-full max-w-7xl px-5 md:px-10">
            <div className="max-w-4xl">
              <h1 className="mt-6 max-w-4xl text-balance text-4xl font-semibold leading-[1.05] tracking-[-0.03em] text-white md:text-6xl xl:text-7xl">
                {siteContent.hero.headline}
              </h1>
              <p className="mt-5 max-w-3xl text-pretty text-base leading-relaxed text-white/75 md:text-lg xl:text-xl">
                {siteContent.hero.subhead}
              </p>



              <div className="mt-8 grid gap-3">
                {siteContent.hero.bullets.map((b) => (
                  <div key={b} className="flex gap-3 text-sm text-white/75">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--brand-primary)]" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>


            </div>


          </div>
        </section>

        <Section
          id="thesis"
          eyebrow={siteContent.thesis.eyebrow}
          title={siteContent.thesis.title}
          description={siteContent.thesis.description}
          className="py-14">
          <div className="grid gap-6">
            {/* 3 pillars (diagram-style) */}
            <GlassCard className="overflow-hidden p-0">
              <div className="grid divide-y divide-white/10 md:grid-cols-3 md:divide-x md:divide-y-0">
                {siteContent.thesis.areas.map((a) => {
                  const Icon = iconMap[a.icon] ?? Cpu;
                  return (
                    <div key={a.id} className="p-6">
                      <div className="flex items-start gap-3">
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-none border border-white/10 bg-black/40">
                          <Icon className="h-5 w-5 text-[var(--brand-primary)]" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{a.title}</p>
                          <p className="mt-2 text-sm leading-relaxed text-white/70">
                            {a.summary}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </GlassCard>

            {/* Mix + decisioning */}
            <GlassCard className="p-7">
              <div className="w-full">
                <p className="text-sm font-semibold text-white">Evaluation</p>
                <p className="mt-3 text-sm leading-relaxed text-white/70">
                  Our focus is balanced across three lanes. We look for early-stage teams positioned
                  to benefit from durable trends, building alongside them with conviction and speed.
                </p>

                <div className="mt-8">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-semibold text-white/70">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-2 bg-[var(--brand-primary)]" />
                      Deep Tech + AI
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-2 bg-white/40" />
                      Enterprise Software
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-2 bg-white/25" />
                      Fintech
                    </span>
                  </div>
                  <StackedMix />
                </div>
              </div>
            </GlassCard>
          </div>
        </Section>

        <Section
          id="services"
          eyebrow={siteContent.services.eyebrow}
          title={siteContent.services.title}
          className="py-14">
          <div className="grid gap-4 md:grid-cols-2">
            {siteContent.services.tracks.map((t) => (
              <GlassCard key={t.id} className="p-6">
                <p className="text-lg font-semibold text-white">{t.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  {t.description}
                </p>
                <ul className="mt-6 grid gap-2 text-sm text-white/75">
                  {t.bullets.map((b) => (
                    <li key={b} className="flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--brand-primary)]" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </GlassCard>
            ))}
          </div>

        </Section>

        <Section
          id="portfolio"
          eyebrow={siteContent.portfolio.eyebrow}
          title={siteContent.portfolio.title}
          description={siteContent.portfolio.description}
          className="py-14">
          <Tabs defaultValue={siteContent.portfolio.tabs[0].key}>
            <TabsList className="w-full justify-start rounded-none bg-transparent p-0">
              {siteContent.portfolio.tabs.map((t) => (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  className="rounded-none border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/70 data-[state=active]:border-[var(--brand-primary)] data-[state=active]:bg-[rgba(0,255,133,0.12)] data-[state=active]:text-white">
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {siteContent.portfolio.tabs.map((t) => (
              <TabsContent key={t.key} value={t.key} className="mt-6">
                <div className="grid gap-4 md:grid-cols-3">
                  {t.items.map((it) => (
                    <GlassCard key={it.name} className="p-6">
                      <p className="text-sm font-semibold text-white">
                        {it.name}
                      </p>
                      <p className="mt-1 text-xs font-semibold tracking-[0.16em] text-white/55 uppercase">
                        {it.sector}
                      </p>
                      <p className="mt-4 text-sm text-white/70">{it.note}</p>
                    </GlassCard>
                  ))}

                  {/* Special card for Past Investments if in the investments tab */}
                  {t.key === "investments" && t.pastInvestments && (
                    <div className="p-6 border border-white/10 bg-transparent">
                      <p className="text-xs font-semibold tracking-[0.16em] text-white/55 uppercase mb-4">
                        Past Investments
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {t.pastInvestments.map((name) => (
                          <span key={name} className="text-sm font-medium text-white/80">
                            {name}
                          </span>
                        ))}
                      </div>
                      <p className="mt-4 text-[10px] italic text-white/40">
                        *Exited or historical positions
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </Section>



        <Section id="contact" eyebrow="Contact" title="Let’s talk" className="py-14">
          <ContactBlock />
        </Section>



        <footer className="border-t border-white/10 py-10">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-5 md:flex-row md:items-center md:justify-between md:px-8">
            <div className="flex items-center">
              <p className="text-xs text-white/60">© {new Date().getFullYear()} Sixth Horizon</p>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm">
              {nav.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  className="text-white/65 transition-colors hover:text-white">
                  {n.label}
                </a>
              ))}
              <a
                href={`mailto:${siteContent.company.email}`}
                className="text-white/65 transition-colors hover:text-white">
                {siteContent.company.email}
              </a>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}

